// // // // //
// GENERAL
// Basic items about the application.
// // // // //
export const COMPANY_NAME = "Odenberg Technologies Ltd";
export const WEBSITE_NAME = "Perio-Bingo";
export const WEBSITE_SLOGAN = "Periodontal Examination and Charting Interface";
export const COPYRIGHT = "Copyright © " + COMPANY_NAME;

// // // // //
// TOOLBAR
// Text on top of the page's navigation menu
// // // // //
export const TOOLBAR_TITLE = "Perio-Bingo";
export const TOOLBAR_DESCRIPTION = "Periodontal Examination and Charting Interface";

// // // // //
// SIDEBAR
// Text on the sidebar on the left side of the page.
// // // // //
export const SIDEBAR_TITLE = `Page selection`;

export const SIDEBAR_OVERVIEW = `Overview`;
export const SIDEBAR_OVERVIEW_TOOLTIP = `General overview of the current state of the application`;

export const SIDEBAR_ABOUT = `About`;
export const SIDEBAR_ABOUT_TOOLTIP = `View info about this application and how to use it`;

export const SIDEBAR_DATA_CONFIG = `System`;
export const SIDEBAR_ADMINISTRATION = `Management`;

// // // // //
// BUTTONS
// Text on the buttons.
// // // // //
export const BUTTON_SIGNIN = `Sign in`;
export const BUTTON_SIGNUP = `Sign up`;
export const BUTTON_SIGNOUT = `Sign out`;
export const BUTTON_NO_ACCOUNT = `No account? Sign up here!`;
export const BUTTON_ALREADY_HAVE_ACCOUNT = `Already have an account?`;

export const BUTTON_RETURN = `Save & Return`;
export const BUTTON_CANCEL = `Cancel`;
export const BUTTON_BACK = `Back`;
export const BUTTON_NEXT = `Next`;
export const BUTTON_MORE_INFO = `More info`;

// // // // //
// SNACKBAR
// Small message pop-ups.
// // // // //
export const SNACKBAR_SUCCESS_SIGNED_IN = `Signed in!`;
export const SNACKBAR_SUCCESS_SIGNED_UP = `Account successfully created!`;

export const SNACKBAR_ERROR_SESSION_EXPIRED = `Session expired - please log back in!`;
export const SNACKBAR_ERROR_SERVER = `Lost connection to server!`;
export const SNACKBAR_ERROR_RESPONSE = `No response from server`;

export const SNACKBAR_ERROR_FORM_INVALID = `Invalid form, check for any errors!`;

export const SNACKBAR_INFO_LOADING = `Loading, please wait...`;

export const SNACKBAR_SUCCESS_CREATED = `New item(s) created!`;
export const SNACKBAR_SUCCESS_UPDATED = `Item(s) updated!`;
export const SNACKBAR_SUCCESS_DELETED = `Item(s) deleted!`;

export const SNACKBAR_ERROR_NO_CHANGES = `No changes detected!`;
export const SNACKBAR_ERROR_PASSWORD_MATCH = `New password doesn't match!`;

export const SNACKBAR_SUCCESS_HIGHLIGHT_REVIEW = `Review saved!`;

// // // // //
// HOME PAGE: INTRODUCTION
// Initial descriptions.
// // // // //
export const HOME_OVERVIEW_TITLE = `Periodontal Charting System`;
export const HOME_OVERVIEW_SUBTITLE = `Digital periodontal examination documentation for dental professionals`;
export const HOME_OVERVIEW_TEXT_1 = `System status:
Version: <version>
Startup: <startup_timestamp:format_date_time>
`;

export const HOME_OVERVIEW_NO_DATA_TEXT_1 = `System status:
Version: <version>
Startup: <startup_timestamp:format_date_time>
`;

export const HOME_OVERVIEW_NO_DATA_TEXT_2 = `No data available!

This means the system is not running.

If the issue persists, please contact the administrator.`;

export const HOME_OVERVIEW_NO_DATA_TEXT_3 = `Welcome to the Periodontal Charting System — a comprehensive digital solution designed to streamline periodontal examinations and improve patient care documentation.

Please create an account or sign in to use the system.`;

// // // // //
// HOME PAGE: SEARCH
// Text used in the search page.
// // // // //
export const SEARCH_TITLE = `Search`;
export const SEARCH_SUBTITLE = `Search for resources and entities.
There are two seach modes: standard and AI-powered search.
Standard search is a simple keyword-based search, while AI-powered search uses an assistant to answer questions.`;

// // // // //
// ABOUT PAGE
// Text about this application.
// // // // //
export const ABOUT_TITLE = `${WEBSITE_NAME}`;
export const ABOUT_SUBTITLE = `${WEBSITE_SLOGAN}`;

export const ABOUT_TEXT_1 = `Welcome to the Periodontal Charting System — a comprehensive digital solution designed to streamline periodontal examinations and improve patient care documentation.`;

export const ABOUT_TEXT_2 = `What is Periodontal Charting?

Periodontal charting is the systematic recording of the health status of the tissues surrounding teeth. This critical diagnostic tool helps dental professionals:
• Monitor gum disease progression over time
• Document baseline periodontal conditions
• Track treatment effectiveness
• Communicate findings with patients and colleagues
• Maintain comprehensive patient records for legal and clinical purposes`;

export const ABOUT_TEXT_3 = `Key Features:
✓ Complete Charting System: Record bleeding sites, probing depths (1-10mm), recession measurements, furcation classifications (I-III), suppuration, plaque presence, and tooth mobility (1-3)
✓ All Tooth Surfaces: Chart all 32 permanent teeth plus 20 deciduous teeth across 8 distinct views — upper/lower jaw, right/left quadrants, and buccal/palatal/lingual surfaces
✓ Missing Teeth & Implants: Track missing teeth and dental implants with dedicated visualization
✓ Flexible Workflows: Optional separate plaque charting for enhanced clinical flexibility
✓ Responsive Design: Optimized for tablets (primary use), desktops, and mobile devices with adaptive column visibility
✓ Comprehensive Views: Each examination displays all 8 charting views side-by-side for complete periodontal assessment
✓ Patient Management: Organize examinations by patient with full history tracking`;

export const ABOUT_TEXT_4 = `Clinical Benefits:
• This system provides dental professionals with a fast, intuitive interface for capturing detailed periodontal data. The color-coded button system allows for rapid data entry during examinations, while the comprehensive view ensures no clinical detail is overlooked.
• The digital format eliminates handwriting legibility issues, reduces charting errors, and enables easy comparison between examinations to track disease progression or treatment success. All data is securely stored and can be accessed instantly for patient consultations or continuing care appointments.`;

export const ABOUT_TEXT_5 = `Technical details below:
The frontend is built using React and Material-UI, with single-page application (SPA) architecture.
The backend is built using Python, with the FastAPI framework and SQLAlchemy as the ORM.
The database is PostgreSQL with a few extensions: timescale.
`;

export const ABOUT_TEXT_6 = `Happy charting!`;

// // // // //
// ENTITIES PAGE
// Text used in the entities page.
// // // // //
export const ENTITIES_TITLE = `Entities`;
export const ENTITIES_SUBTITLE = `Create, view, and edit entities.`;
export const NO_ENTITIES_FOUND = `No entities found! Add a new resource to get started.`;

export const ENTITY_VIEW_TITLE = `View entity`;
export const ENTITY_VIEW_SUBTITLE = `View the formatted entity and its quotes.`;
export const NO_ENTITY_FOUND = `This entity could not be found!

This might be due to the entity not being fully processed yet, the resource having been being deleted, or simply a typo in the URL.`;

export const ENTITY_EDIT_TITLE = `Edit entity`;
export const ENTITY_EDIT_SUBTITLE = `Review and edit the entity before approving it.`;

export const ENTITY_EDIT_VIEW_TITLE = `View previous entity`;
export const ENTITY_EDIT_VIEW_SUBTITLE = `View the previous version of the formatted entity.
This is either the initial formatted entity, created during resource creation, or the most recent entity which has been approved.`;

// // // // //
// EXAMINATIONS PAGE
// Text used in the examinations page.
// // // // //
export const EXAMINATIONS_TITLE = `Examinations`;
export const EXAMINATIONS_SUBTITLE = `Create, view, and edit examinations.`;
export const NO_EXAMINATIONS_FOUND = `No examinations found! Add a new resource to get started.`;

export const EXAMINATION_VIEW_TITLE = `View examination`;
export const EXAMINATION_VIEW_SUBTITLE = `View the recorded chart, its summary statistics and the top-down chart views.`;
export const NO_EXAMINATION_FOUND = `This examination could not be found!

This might be due to the examination not being fully processed yet, the resource having been being deleted, or simply a typo in the URL.`;

export const EXAMINATION_EDIT_TITLE = `Edit examination`;
export const EXAMINATION_EDIT_SUBTITLE = `Review and edit the recorded measurements, then save the changes.`;

export const EXAMINATION_EDIT_VIEW_TITLE = `View previous examination`;
export const EXAMINATION_EDIT_VIEW_SUBTITLE = `View the previous version of the formatted examination.
This is either the initial formatted examination, created during resource creation, or the most recent examination which has been approved.`;

// // // // //
// NEW EXAMINATION PAGE
// Text used in the examinations page.
// // // // //
export const EXAMINATION_CREATE_TITLE = `New examination`;
export const EXAMINATION_CREATE_SUBTITLE = `Create a new periodontal examination.`;

export const EXAMINATION_CREATE_ABOUT = `This section will guide you through creating a new periodontal examination.

The examination is split into multiple steps:
1. Patient Data
2. Dental Chart - confirm missing teeth and implants
3. Plaque, calculus, and erythema fast charts
4. Periodontal Measurements: Upper jaw
5. Periodontal Measurements: Lower jaw
6. Review and Submit

You can navigate between the steps using the "Back" and "Next" buttons at the bottom of the page.`;

// // // // //
// PATIENTS PAGE
// Text used in the patients page.
// // // // //
export const PATIENTS_TITLE = `Patients`;
export const PATIENTS_SUBTITLE = `Create, view, and edit patients.`;
export const NO_PATIENTS_FOUND = `No patients found! Add a new resource to get started.`;

export const PATIENT_VIEW_TITLE = `Patient record`;
export const PATIENT_VIEW_SUBTITLE = `Patient details and the full history of their periodontal examinations.`;
export const NO_PATIENT_FOUND = `This patient could not be found!

This might be due to the patient having been deleted, or simply a typo in the URL.`;

export const PATIENT_EDIT_TITLE = `Edit patient`;
export const PATIENT_EDIT_SUBTITLE = `Review and edit the patient details, then save the changes.`;

export const PATIENT_EDIT_VIEW_TITLE = `View previous patient`;
export const PATIENT_EDIT_VIEW_SUBTITLE = `View the previous version of the formatted patient.
This is either the initial formatted patient, created during resource creation, or the most recent patient which has been approved.`;

// // // // //
// USERS PAGE
// Text used in the users page.
// // // // //
export const USERS_TITLE = `Users`;
export const USERS_SUBTITLE = `View, approve and remove users.
Users are people who have access to the system, and can be assigned different roles.`;
export const NO_USERS_FOUND = `No users found!`;

// // // // //
// UNEXPECTED ERROR PAGE
// Text on the error page if some unexpected exception occurred.
// // // // //
export const UNEXPECTED_ERROR_DESCRIPTION = `Something went wrong!

Make sure you are logged in, or are authorized to access this page.
Otherwise, please let us know of the error.`;

export const UNEXPECTED_ERROR_BUTTON_1 = `Home`;
export const UNEXPECTED_ERROR_BUTTON_2 = `Sign in`;

// // // // //
// 404 NOT FOUND PAGE
// Text on the error page if some unkown URL was queried.
// // // // //
export const NOT_FOUND_DESCRIPTION = `This page doesn't exist.

Check the URL for any typos.`;

export const NOT_FOUND_BUTTON = `Home`;

// // // // //
// AUTH PAGES
// Text used in sign up or sign in pages.
// // // // //
export const LOGGED_OUT = `You have been logged out.`;
export const NEXT_TIME = `Until next time!`;
export const LOG_BACK_IN = `You can log back in anytime.`;

// // // // //
// ACCOUNT PAGE
// User info, how-tos, etc.
// // // // //
export const ACCOUNT_TITLE = `Account`;
export const ACCOUNT_SUBTITLE = `Welcome, <username>!
To log out, click the exit icon on the top right of the page.`;

export const ACCOUNT_TEXT_1 = `User ID: <id>
Role: <role>
Username: <username>
Email: <email>

Account data can be modified below.`;

export const USER_UPDATE_TITLE = `Edit account information`;
export const USER_UPDATE_SUBTITLE = `Leave fields empty to keep their current values.`;

// // // // //
// TOUR STEPS
// Descriptions for the tour steps.
// // // // //

// About tour.
export const TOUR_ABOUT_1 = `Welcome to Periodontal Chart, a tool designed to help you with periodontal examination and charting.`;

export const TOUR_ABOUT_2 = `The icons on the top right corner include features like changing the color theme and logging out.

The question mark icon opens the tour guide for that page. Hover over the icons to see their descriptions.`;

export const TOUR_ABOUT_3 = `The drawer on the left side contains the main navigation menu. Click on the icons to navigate to different pages.

Hover over the menu links to see short page descriptions.`;

export const TOUR_ABOUT_4 = `Each page starts with a title and a subtitle, describing the main features of the page.`;

export const TOUR_ABOUT_5 = `Whenever the tour guide icon on the top right is enabled, click on it to start a short tour describing the selected page's features more in-depth.`;

export const TOUR_ABOUT_6 = `There are three main features of the system:

1. Creating new periodontal examinations, which involves inputting patient data and charting periodontal measurements.

2. Viewing and managing existing examinations, where you can edit patient information and update charts as needed.

3. A practice patient on this About page, for learning the chart without writing anything to a real patient.
`;

export const TOUR_ABOUT_7 = `Start a practice run here.

Click Open practice patient, choose the one practice patient, and chart a full examination. At the end you see the same charts and statistics as a saved examination. The chart resets each time you open it, and nothing is written to the patient records.`;

export const TOUR_ABOUT_8 = `This is the examinations table. Every saved chart is listed here.`;

export const TOUR_ABOUT_NEW_EXAMINATION = `New examination starts a chart. The next step asks you to choose the patient.`;

export const TOUR_ABOUT_9 = `Edit opens the selected examination so the chart can be changed.

Select one row first. The button stays quiet until then.`;

export const TOUR_ABOUT_10 = `Delete removes the selected examinations.

Only an admin can confirm a deletion.`;

export const TOUR_ABOUT_11 = `Move sends the selected examination to a different patient, without charting it again.

Select one row, then choose the patient to receive it.`;

export const TOUR_ABOUT_12 = `The patients table lists everyone with a record.

From here you can add a patient, open one, or look for names that are easy to confuse.`;

export const TOUR_ABOUT_13 = `Find similar patient names compares the list and groups names that match about 75%, ignoring capitals and spaces.

Use it before adding someone who may already be in the table.`;

export const TOUR_ABOUT_14 = `Feedback and bug reports are most welcome.

Happy charting!`;

export const ROLE_DESCRIPTION = {
    USER: "A user can create and edit patients and examinations, and can move an examination to another patient. A user cannot manage other accounts, and cannot delete patients or examinations.",
    ADMIN: "An admin can do everything a user can. An admin can also create accounts, change roles, turn accounts on or off, reset an authenticator, and delete patients or examinations.",
    SYSTEM: "System is a service account. Its sign-in token does not expire, and it cannot open the charting pages. Choose User or Admin for a person."
};

export const PATIENT_DETAILS_HINT = `A patient ID is enough, so a record can stay anonymous. First name, last name, and birth date are optional when an ID is filled in. If the ID is left blank, those three fields are required instead. Notes are always optional.`;
